import { memo, useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import Form from "../../components/Form";
import WeaningForm from "../../components/forms/WeaningForm";
import Spinner from "../../components/Spinner";
import useListStyles from "../../components/styles/List";
import { useSnackbar } from "../../utils/index.js";
import { getOwners } from "../../api/index.js";

const WeaningDate = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [seasonDropdownValues, setSeasonDropdownValues] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [step, setStep] = useState(0);

    const classes = useListStyles();
    const { error } = useSnackbar();

    const generateSeasonDropdownValues = useCallback(() => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const latestSeasonStartYear = currentMonth >= 8 ? currentYear : currentYear - 1;

        const seasons = [];

        for (let year = 2000; year <= latestSeasonStartYear; year++) {
            const startDate = `1/8/${year}`;
            const endDate = `31/7/${year + 1}`;
            seasons.push({
                value: `${startDate} - ${endDate}`,
                text: `${startDate} - ${endDate}`,
                startYear: year,
                endYear: year + 1
            });
        }

        return seasons.reverse();
    }, []);

    const parseSeasonToDates = useCallback((seasonString) => {
        const match = seasonString.match(/1\/8\/(\d{4}) - 31\/7\/(\d{4})/);
        if (match) {
            const startYear = parseInt(match[1], 10);
            const endYear = parseInt(match[2], 10);

            const startDate = new Date(startYear, 7, 1);
            const endDate = new Date(endYear, 6, 31);

            return { startDate, endDate };
        }
        return null;
    }, []);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(owners.map((owner) => ({
                value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
            })));

            const seasons = generateSeasonDropdownValues();
            setSeasonDropdownValues(seasons);
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [generateSeasonDropdownValues, error]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "dropdown",
            id: "season",
            items: seasonDropdownValues,
            placeholder: "Περίοδος Τοκετών",
            defaultValue: seasonDropdownValues[0]?.value || "",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο"
        },
    ];

    const handleSubmitStep0 = async (values) => {
        setIsLoading(true);

        const selOwner = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selOwner);

        const parsedDates = parseSeasonToDates(values.season);
        if (!parsedDates) {
            error("Σφάλμα στην επεξεργασία της επιλεγμένης περιόδου");
            setIsLoading(false);
            return;
        }

        setSelectedStartDate(parsedDates.startDate);
        setSelectedEndDate(parsedDates.endDate);

        setStep(1);
        setIsLoading(false);
    };

    return (
        <>
            <Spinner open={isLoading} />
            {step === 0 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Λήξη Περιόδου Αμνών
                        </Typography>
                        <Typography variant="subtitle1" sx={{ margin: "1rem", marginTop: "0rem" }}>
                            Επιλέξτε την Εκτροφή και την περίοδο τοκετών:
                        </Typography>
                        <Form
                            content={ownerFormContent}
                            onSubmit={handleSubmitStep0}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 1 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <WeaningForm
                            selectedOwner={selectedOwner}
                            selectedStartDate={selectedStartDate}
                            selectedEndDate={selectedEndDate}
                            setIsAnimalSelected={() => setStep(0)}
                        />
                    </Box>
                </Box>
            )}
        </>
    );
};

export default memo(WeaningDate);